import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { emptyDesignation } from '../utils/userProfileForm';

function Row({ entry, index, onChange, onRemove }) {
  const set = (patch) => onChange(index, { ...entry, ...patch });
  return (
    <div className="rounded-lg border bg-zinc-50/80 p-3 space-y-3" data-testid={`designation-row-${index}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold text-zinc-600">
          {entry.is_current ? 'Current' : 'Past'} designation
        </p>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-zinc-400 hover:text-red-600 p-1"
          aria-label="Remove designation"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs text-zinc-600">Title / designation</Label>
          <Input
            value={entry.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="Senior Medical Officer, Faculty In-Charge…"
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs text-zinc-600">Organisation / unit</Label>
          <Input
            value={entry.organization}
            onChange={(e) => set({ organization: e.target.value })}
            placeholder="IIT Hyderabad Hospital"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-zinc-600">From (YYYY-MM)</Label>
          <Input
            value={entry.from_date}
            onChange={(e) => set({ from_date: e.target.value })}
            placeholder="2020-01"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-zinc-600">To (YYYY-MM)</Label>
          <Input
            value={entry.is_current ? '' : entry.to_date}
            onChange={(e) => set({ to_date: e.target.value })}
            placeholder="Present"
            disabled={entry.is_current}
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-zinc-300"
          checked={Boolean(entry.is_current)}
          onChange={(e) => set({ is_current: e.target.checked, to_date: e.target.checked ? '' : entry.to_date })}
        />
        This is a current designation
      </label>
    </div>
  );
}

/** Multi-entry current + past designations editor. */
export default function DesignationListEditor({ designations = [], onChange }) {
  const update = (index, next) => {
    const list = designations.slice();
    list[index] = next;
    onChange(list);
  };
  const remove = (index) => onChange(designations.filter((_, i) => i !== index));
  const add = (isCurrent) => onChange([...designations, { ...emptyDesignation(), is_current: isCurrent }]);

  return (
    <div className="space-y-3" data-testid="designation-list-editor">
      {designations.length === 0 ? (
        <p className="text-sm text-zinc-500">No designations yet. Add current or past roles.</p>
      ) : (
        designations.map((entry, i) => (
          <Row key={i} entry={entry} index={i} onChange={update} onRemove={remove} />
        ))
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => add(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add current
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => add(false)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add past
        </Button>
      </div>
    </div>
  );
}
